import React, { useState, useEffect } from 'react'

interface ScheduleBuilderProps {
  currentSchedule: string
  onScheduleChange: (schedule: string) => void
  onUpdateSchedule: () => void
  isSubmitting: boolean
}

type ScheduleType = 'daily' | 'weekly' | 'interval' | 'custom'

interface ScheduleConfig {
  type: ScheduleType
  time: { hour: number; minute: number }
  weekdays: number[]  // 0 = Sunday, 1 = Monday, etc.
  intervalHours: number
}

const ScheduleBuilder: React.FC<ScheduleBuilderProps> = ({
  currentSchedule,
  onScheduleChange,
  onUpdateSchedule,
  isSubmitting
}) => {
  const [config, setConfig] = useState<ScheduleConfig>({
    type: 'weekly',
    time: { hour: 9, minute: 0 },
    weekdays: [1, 2, 3, 4, 5], // Weekdays default
    intervalHours: 6
  })

  const [cronExpression, setCronExpression] = useState('')

  // Parse existing cron to populate config
  useEffect(() => {
    parseCronToConfig(currentSchedule)
  }, [currentSchedule])

  // Generate cron expression from config
  useEffect(() => {
    const cron = generateCronExpression(config)
    setCronExpression(cron)
    onScheduleChange(cron)
  }, [config])

  const parseCronToConfig = (cron: string) => {
    const parts = cron.trim().split(/\s+/)
    if (parts.length !== 5) return

    const [minute, hour, _day, _month, weekday] = parts

    const parsedConfig: ScheduleConfig = {
      type: 'custom',
      time: { hour: 9, minute: 0 },
      weekdays: [1, 2, 3, 4, 5],
      intervalHours: 6
    }

    // Parse minute and hour
    if (minute !== '*' && hour !== '*') {
      parsedConfig.time.minute = parseInt(minute) || 0
      parsedConfig.time.hour = parseInt(hour) || 9
    }

    // Detect schedule type
    if (hour.includes('*/')) {
      // Interval schedule
      parsedConfig.type = 'interval'
      parsedConfig.intervalHours = parseInt(hour.replace('*/', '')) || 6
    } else if (weekday === '*') {
      // Daily schedule
      parsedConfig.type = 'daily'
    } else if (weekday.includes('-') || weekday.includes(',')) {
      // Weekly schedule
      parsedConfig.type = 'weekly'
      if (weekday === '1-5') {
        parsedConfig.weekdays = [1, 2, 3, 4, 5]
      } else if (weekday.includes(',')) {
        parsedConfig.weekdays = weekday.split(',').map(d => parseInt(d)).filter(d => !isNaN(d))
      }
    }

    setConfig(parsedConfig)
  }

  const generateCronExpression = (config: ScheduleConfig): string => {
    const { type, time, weekdays, intervalHours } = config

    switch (type) {
      case 'daily':
        return `${time.minute} ${time.hour} * * *`

      case 'weekly':
        const days = weekdays.sort().join(',')
        return `${time.minute} ${time.hour} * * ${days}`

      case 'interval':
        return `0 */${intervalHours} * * *`

      case 'custom':
        return cronExpression || currentSchedule

      default:
        return '0 9 * * 1-5'
    }
  }

  const weekdayNames = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ]

  const toggleWeekday = (day: number) => {
    setConfig(prev => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter(d => d !== day)
        : [...prev.weekdays, day].sort()
    }))
  }

  const timeToString = (hour: number, minute: number) => {
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    const displayMinute = minute.toString().padStart(2, '0')
    return `${displayHour}:${displayMinute} ${period}`
  }

  const isScheduleChanged = cronExpression !== currentSchedule

  return (
    <div className="space-y-6">
      {/* Schedule Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Schedule Type
        </label>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[
            { 
              type: 'weekly' as ScheduleType, 
              label: 'Weekly', 
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              )
            },
            { 
              type: 'daily' as ScheduleType, 
              label: 'Daily', 
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )
            },
            { 
              type: 'interval' as ScheduleType, 
              label: 'Interval', 
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )
            },
            { 
              type: 'custom' as ScheduleType, 
              label: 'Custom', 
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )
            }
          ].map(({ type, label, icon }) => (
            <button
              key={type}
              onClick={() => setConfig(prev => ({ ...prev, type }))}
              className={`p-4 border-2 rounded-lg transition-all ${
                config.type === type
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <div className="flex justify-center mb-2">{icon}</div>
              <div className="font-medium">{label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Time Selection (for daily and weekly) */}
      {(config.type === 'daily' || config.type === 'weekly') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Time (EST)
          </label>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Hour:</label>
              <select
                value={config.time.hour}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  time: { ...prev.time, hour: parseInt(e.target.value) }
                }))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {timeToString(i, 0).split(':')[0]} {i >= 12 ? 'PM' : 'AM'}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Minute:</label>
              <select
                value={config.time.minute}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  time: { ...prev.time, minute: parseInt(e.target.value) }
                }))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[0, 15, 30, 45].map(minute => (
                  <option key={minute} value={minute}>
                    {minute.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-lg font-medium text-blue-600">
              {timeToString(config.time.hour, config.time.minute)} EST
            </div>
          </div>
        </div>
      )}

      {/* Days Selection (for weekly) */}
      {config.type === 'weekly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Days of Week
          </label>
          <div className="grid grid-cols-7 gap-2">
            {weekdayNames.map((name, index) => (
              <button
                key={index}
                onClick={() => toggleWeekday(index)}
                className={`p-3 border-2 rounded-lg transition-all text-sm font-medium ${
                  config.weekdays.includes(index)
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {name.slice(0, 3)}
              </button>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={() => setConfig(prev => ({ ...prev, weekdays: [1, 2, 3, 4, 5] }))}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full"
            >
              Weekdays
            </button>
            <button
              onClick={() => setConfig(prev => ({ ...prev, weekdays: [0, 6] }))}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full"
            >
              Weekends
            </button>
            <button
              onClick={() => setConfig(prev => ({ ...prev, weekdays: [0, 1, 2, 3, 4, 5, 6] }))}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full"
            >
              Every Day
            </button>
          </div>
        </div>
      )}

      {/* Interval Selection */}
      {config.type === 'interval' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Run Every
          </label>
          <div className="flex items-center space-x-3">
            <select
              value={config.intervalHours}
              onChange={(e) => setConfig(prev => ({ ...prev, intervalHours: parseInt(e.target.value) }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[1, 2, 3, 4, 6, 8, 12].map(hours => (
                <option key={hours} value={hours}>
                  {hours} hour{hours !== 1 ? 's' : ''}
                </option>
              ))}
            </select>
            <span className="text-gray-600">
              (runs at the top of each hour)
            </span>
          </div>
        </div>
      )}

      {/* Custom Cron Input */}
      {config.type === 'custom' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Custom Cron Expression
          </label>
          <input
            type="text"
            value={cronExpression}
            onChange={(e) => setCronExpression(e.target.value)}
            placeholder="0 9 * * 1-5"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-sm text-gray-500 mt-1">
            Format: minute hour day month weekday
          </p>
        </div>
      )}

      {/* Preview */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Schedule Preview</h4>
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-medium">Cron Expression:</span>{' '}
            <code className="bg-white px-2 py-1 rounded border">{cronExpression}</code>
          </div>
          <div className="text-gray-600">
            <span className="font-medium">Description:</span>{' '}
            {config.type === 'daily' && `Every day at ${timeToString(config.time.hour, config.time.minute)} EST`}
            {config.type === 'weekly' && `${config.weekdays.map(d => weekdayNames[d].slice(0, 3)).join(', ')} at ${timeToString(config.time.hour, config.time.minute)} EST`}
            {config.type === 'interval' && `Every ${config.intervalHours} hour${config.intervalHours !== 1 ? 's' : ''}`}
            {config.type === 'custom' && 'Custom cron expression'}
          </div>
          {currentSchedule !== cronExpression && (
            <div className="text-amber-600">
              <span className="font-medium">Current:</span>{' '}
              <code className="bg-white px-2 py-1 rounded border">{currentSchedule}</code>
            </div>
          )}
        </div>
      </div>

      {/* Update Button */}
      <div className="flex items-center space-x-3">
        <button
          onClick={onUpdateSchedule}
          disabled={isSubmitting || !isScheduleChanged}
          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Updating...' : 'Update Schedule'}
        </button>
        
        {isScheduleChanged && (
          <button
            onClick={() => parseCronToConfig(currentSchedule)}
            className="text-gray-600 hover:text-gray-800 px-3 py-2 text-sm"
          >
            Reset to Current
          </button>
        )}
      </div>
    </div>
  )
}

export default ScheduleBuilder 